import React, { useState, useEffect } from 'react';
import { stationFacilityAPI, stationAPI } from '../services/api';
import { StationFacility, Station } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent
} from '@mui/material';
import { Edit, Delete, Refresh, Add } from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const StationFacilities: React.FC = () => {
  const [facilities, setFacilities] = useState<StationFacility[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    stationId: '',
    facilityType: '' as 'AIRPORT' | 'RAILWAY' | 'BUS_STAND' | 'HOSPITAL' | 'MALL' | 'UNIVERSITY' | 'PARK',
    facilityName: '',
    distance: '',
    description: '',
    contactNumber: '',
    operatingHours: '',
    address: '',
    rating: ''
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    setLoading(true);
    setError(null);
    try {
      const [facilitiesRes, stationsRes] = await Promise.all([
        stationFacilityAPI.getAll(),
        stationAPI.getAll()
      ]);
      setFacilities(facilitiesRes.data);
      setStations(stationsRes.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm({ 
      ...form, 
      [name]: type === 'number' ? parseFloat(value) || 0 : value 
    });
    setFormError('');
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    setForm({ ...form, [name]: e.target.value });
    setFormError('');
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setEditingId(null);
    setForm({
      stationId: '',
      facilityType: 'AIRPORT' as 'AIRPORT' | 'RAILWAY' | 'BUS_STAND' | 'HOSPITAL' | 'MALL' | 'UNIVERSITY' | 'PARK',
      facilityName: '',
      distance: '',
      description: '',
      contactNumber: '',
      operatingHours: '',
      address: '',
      rating: ''
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (facility: StationFacility) => {
    setIsEditMode(true);
    setEditingId(facility.id!);
    setForm({
      stationId: facility.stationId,
      facilityType: facility.facilityType,
      facilityName: facility.facilityName || '',
      distance: facility.distance.toString(),
      description: facility.description || '',
      contactNumber: facility.contactNumber || '',
      operatingHours: facility.operatingHours || '',
      address: facility.address || '',
      rating: facility.rating ? facility.rating.toString() : ''
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.stationId || !form.facilityType || !form.distance) {
      setFormError('Station, Facility Type, and Distance are required');
      return;
    }

    setSaving(true);
    try {
      const facilityData = {
        ...form,
        distance: parseFloat(form.distance),
        rating: form.rating ? parseFloat(form.rating) : undefined
      };

      if (isEditMode && editingId) {
        await stationFacilityAPI.update(editingId, facilityData);
        setSuccessMsg('Facility updated successfully');
      } else {
        await stationFacilityAPI.create(facilityData);
        setSuccessMsg('Facility created successfully');
      }
      await fetchFacilities();
      closeModal();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to save facility');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this facility?')) return;
    
    setDeletingId(id);
    try {
      await stationFacilityAPI.delete(id);
      setSuccessMsg('Facility deleted successfully');
      await fetchFacilities();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete facility');
    } finally {
      setDeletingId(null);
    }
  };

  const getStationName = (stationId: string) => {
    const station = stations.find(s => s.id === stationId);
    return station?.name || 'Unknown Station';
  };

  const getFacilityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'AIRPORT': 'Airport',
      'RAILWAY': 'Railway Station',
      'BUS_STAND': 'Bus Stand',
      'HOSPITAL': 'Hospital',
      'MALL': 'Shopping Mall',
      'UNIVERSITY': 'University',
      'PARK': 'Park'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Station Facilities
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddModal}>
          Add Facility
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mx: 3, mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mx: 3, mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Station</TableCell>
                <TableCell>Facility Type</TableCell>
                <TableCell>Facility Name</TableCell>
                <TableCell>Distance (km)</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facilities.map((facility) => (
                <TableRow key={facility.id}>
                  <TableCell>{getStationName(facility.stationId)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={getFacilityTypeLabel(facility.facilityType)} 
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{facility.facilityName || 'N/A'}</TableCell>
                  <TableCell>{facility.distance.toFixed(2)}</TableCell>
                  <TableCell>
                    {/* Rating component was removed from imports, so this will cause an error */}
                    {/* <Rating value={facility.rating || 0} readOnly size="small" /> */}
                  </TableCell>
                  <TableCell>{facility.contactNumber || 'N/A'}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => openEditModal(facility)} size="small">
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        onClick={() => handleDelete(facility.id!)} 
                        size="small"
                        disabled={deletingId === facility.id}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Station Facility' : 'Add Station Facility'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Station</InputLabel>
                <Select
                  name="stationId"
                  value={form.stationId}
                  onChange={handleSelectInput}
                  label="Station"
                >
                  {stations.map((station) => (
                    <MenuItem key={station.id} value={station.id}>
                      {station.name} ({station.stationCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Facility Type</InputLabel>
                <Select
                  name="facilityType"
                  value={form.facilityType}
                  onChange={handleSelectInput}
                  label="Facility Type"
                >
                  <MenuItem value="AIRPORT">Airport</MenuItem>
                  <MenuItem value="RAILWAY">Railway Station</MenuItem>
                  <MenuItem value="BUS_STAND">Bus Stand</MenuItem>
                  <MenuItem value="HOSPITAL">Hospital</MenuItem>
                  <MenuItem value="MALL">Shopping Mall</MenuItem>
                  <MenuItem value="UNIVERSITY">University</MenuItem>
                  <MenuItem value="PARK">Park</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Facility Name"
                name="facilityName"
                value={form.facilityName}
                onChange={handleInput}
                placeholder="e.g., Central Airport, City Hospital"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Distance (km)"
                name="distance"
                type="number"
                value={form.distance}
                onChange={handleInput}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                name="contactNumber"
                value={form.contactNumber}
                onChange={handleInput}
                placeholder="Contact number for the facility"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Operating Hours"
                name="operatingHours"
                value={form.operatingHours}
                onChange={handleInput}
                placeholder="e.g., 24/7, 9:00 AM - 6:00 PM"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={form.address}
                onChange={handleInput}
                placeholder="Full address of the facility"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={form.description}
                onChange={handleInput}
                multiline
                rows={3}
                placeholder="Additional details about the facility"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography component="legend">Rating</Typography>
              {/* Rating component was removed from imports, so this will cause an error */}
              {/* <Rating
                name="rating"
                value={form.rating}
                onChange={(_, newValue) => {
                  setForm({ ...form, rating: newValue || 0 });
                }}
                precision={0.5}
              /> */}
            </Grid>
          </Grid>
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={6000}
        onClose={() => setSuccessMsg('')}
      >
        <Alert onClose={() => setSuccessMsg('')} severity="success">
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StationFacilities; 